import React, { useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';

import { Expand } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { definitions, operations } from '@luxuryescapes/contract-svc-promo';
import { getRegionCodes } from '@luxuryescapes/lib-regions';

import useCurrentTenant from '~/hooks/useCurrentTenant';

import { createPromoDisplayConfig, updatePromoDisplayConfig } from '~/services/PromoService';

import usePromoMeta from '../Promo/hooks/usePromoMeta';

import PromoDisplayConfigRules from './PromoDisplayConfigRules';

type AuthState = 'logged_in' | 'logged_out' | 'logged_in_and_out' | null;
type DisplayMode = 'appear_once' | 'persistence' | null;

const isValidQueryParamsRegex = /^[^\s]+$/;

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  fetchData: () => void;
  editableConfig?: operations['updatePromoDisplayConfig']['parameters']['body']['PromoDisplayConfig'];
}

function PromoDisplayConfigForm({ open, setOpen, fetchData, editableConfig }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [displayMode, setDisplayMode] = useState<DisplayMode>(editableConfig?.display_mode ?? 'appear_once');
  const [authState, setAuthState] = useState<AuthState>(editableConfig?.auth_state ?? 'logged_in_and_out');
  const [region, setRegion] = useState<string>(editableConfig?.region ?? 'AU');
  const [ruleJSON, setRuleJSON] = useState<definitions['RuleJson'] | null>(editableConfig?.rule_json ?? null);
  const isEditing = !!editableConfig && !!editableConfig.promo_display_config_id;
  const isCloning = !!editableConfig && !editableConfig.promo_display_config_id;
  const tenant = useCurrentTenant();

  const handleClose = (reason: string) => {
    if (reason === 'quit' || (reason === 'backdropClick' && !isEditing)) {
      enqueueSnackbar('Are you sure you want to quit creating a promo display config?', {
        variant: 'warning',
        action: (
          <Button color="inherit" size="small" onClick={() => setOpen(false)}>
            Quit
          </Button>
        ),
      });
      return;
    }
    setOpen(false);
  };

  const { promoMeta, isPromoMetaLoading } = usePromoMeta();

  // Track the last synced ID to prevent clearing user changes during editing
  const [lastSyncedConfigId, setLastSyncedConfigId] = useState<string | undefined>(
    editableConfig?.promo_display_config_id,
  );

  // Sync state when editableConfig changes (only on initial load or when ID changes)
  useEffect(() => {
    // Only sync if we're loading a different config (ID changed) or initial load
    // This prevents clearing user's unsaved changes during editing
    if (editableConfig?.promo_display_config_id !== lastSyncedConfigId) {
      setRuleJSON(editableConfig?.rule_json ?? null);
      setAuthState(editableConfig?.auth_state ?? 'logged_in_and_out');
      setDisplayMode(editableConfig?.display_mode ?? 'appear_once');
      setRegion(editableConfig?.region ?? 'AU');
      setLastSyncedConfigId(editableConfig?.promo_display_config_id);
    }
  }, [editableConfig?.promo_display_config_id, lastSyncedConfigId]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<Record<string, unknown>>({
    defaultValues: {
      code_name: editableConfig?.code_name || '',
      url_path: editableConfig?.url_path || '',
      source: editableConfig?.source || '',
      medium: editableConfig?.medium || '',
      campaign: editableConfig?.campaign || '',
      header_text: editableConfig?.header_text || '',
      body_text: editableConfig?.body_text || '',
      display_mode: editableConfig?.display_mode || 'appear_once',
      delay_duration: editableConfig?.delay_duration || 0,
      offer_mpv_threshold: editableConfig?.offer_mpv_threshold || 0,
    },
  });

  const handleCreate = useCallback(
    async (data) => {
      try {
        await createPromoDisplayConfig({
          ...data,
          brand: tenant.tenant.brand,
          region: region,
          display_mode: displayMode,
          auth_state: authState,
          delay_duration: Number(data.delay_duration),
          offer_mpv_threshold: Number(data.offer_mpv_threshold),
          code_name: data.code_name?.length > 0 ? data.code_name : editableConfig?.code_name,
          targeted_promo_audience_length: undefined,
          rule_json: ruleJSON,
        });
      } catch (error) {
        enqueueSnackbar(`Promo Display Config creation failed: ${JSON.stringify(error)}`, { variant: 'error' });
        return;
      }

      reset();
      fetchData();
      handleClose('create');
      enqueueSnackbar(
        isCloning ? 'Promo Display Config cloned successfully' : 'Promo Display Config created successfully',
        { variant: 'success' },
      );
    },
    [
      tenant.tenant.brand,
      region,
      displayMode,
      authState,
      ruleJSON,
      reset,
      fetchData,
      handleClose,
      enqueueSnackbar,
      isCloning,
    ],
  );

  const handleEdit = useCallback(
    async (data) => {
      let response;
      try {
        response = await updatePromoDisplayConfig({
          ...data,
          brand: tenant.tenant.brand,
          promo_display_config_id: editableConfig?.promo_display_config_id,
          region: region,
          display_mode: displayMode,
          auth_state: authState,
          delay_duration: Number(data.delay_duration),
          code_name: data.code_name,
          offer_mpv_threshold: Number(data.offer_mpv_threshold),
          rule_json: ruleJSON,
          targeted_promo_audience_length: undefined,
        });
      } catch (error) {
        enqueueSnackbar(`Promo Display Config edit failed: ${JSON.stringify(error)}`, { variant: 'error' });
        return;
      }

      reset();

      // Update ruleJSON from response if available, otherwise keep the current state
      // The response is the parsed JSON from the API, so check for rule_json directly
      if (response?.rule_json) {
        setRuleJSON(response.rule_json);
      }
      // If response doesn't have rule_json, keep the current ruleJSON state

      fetchData();
      handleClose('edit');
      enqueueSnackbar('Promo Display Config edited successfully', { variant: 'success' });
    },
    [
      enqueueSnackbar,
      fetchData,
      reset,
      tenant.tenant.brand,
      region,
      displayMode,
      authState,
      ruleJSON,
      editableConfig?.promo_display_config_id,
    ],
  );

  return (
    <>
      <Dialog
        fullWidth
        maxWidth="xl"
        open
        onClose={(_, reason) => handleClose(reason)}
        component="form"
        onSubmit={handleSubmit(isEditing ? handleEdit : handleCreate)}
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="actual-title">
          {isEditing ? 'Edit' : isCloning ? 'Clone' : 'Create'} a Promo Display Config
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                id="code_name"
                label="Code Name"
                variant="outlined"
                error={!!errors.code_name}
                helperText={errors.code_name?.message}
                {...register('code_name', {
                  required: 'Code name is required',
                })}
                autoComplete="off"
                disabled={isEditing && !isCloning}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6">Targeting Elements</Typography>
            </Grid>
            <Grid item xs={12}>
              <Stack spacing={2}>
                <TextField
                  id="url_path"
                  label="URL Path"
                  variant="outlined"
                  required
                  {...register('url_path', {
                    required: 'URL path is required',
                  })}
                  error={!!errors.url_path}
                  helperText={errors.url_path?.message}
                  autoComplete="off"
                />
                <Autocomplete
                  id="region"
                  options={getRegionCodes()}
                  value={region}
                  onChange={(_, newValue) => {
                    setRegion(newValue || 'AU');
                  }}
                  renderInput={(params) => <TextField {...params} label="Region" />}
                />
                <Autocomplete
                  id="auth_state"
                  options={['logged_in', 'logged_out', 'logged_in_and_out']}
                  value={authState}
                  onChange={(_, newValue) => {
                    setAuthState(newValue as AuthState);
                  }}
                  renderInput={(params) => <TextField {...params} label="Auth State" required />}
                />
              </Stack>
            </Grid>
          </Grid>

          <Grid container spacing={2} marginTop={1}>
            <Grid item xs={4}>
              <TextField
                id="source"
                label="Source"
                variant="outlined"
                {...register('source', {
                  pattern: {
                    value: isValidQueryParamsRegex,
                    message: 'Source must be a valid utm_source',
                  },
                })}
                error={!!errors.source}
                helperText={errors.source?.message}
                autoComplete="off"
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                id="medium"
                label="Medium"
                variant="outlined"
                {...register('medium', {
                  pattern: {
                    value: isValidQueryParamsRegex,
                    message: 'Medium must be a valid utm_medium',
                  },
                })}
                error={!!errors.medium}
                helperText={errors.medium?.message}
                autoComplete="off"
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                id="campaign"
                label="Campaign"
                variant="outlined"
                {...register('campaign', {
                  pattern: {
                    value: isValidQueryParamsRegex,
                    message: 'Campaign must be a valid utm_campaign',
                  },
                })}
                error={!!errors.campaign}
                helperText={errors.campaign?.message}
                autoComplete="off"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6">Display Elements</Typography>
            </Grid>
            <Grid item xs={12}>
              <Stack spacing={2}>
                <TextField
                  id="header_text"
                  label="Header Text"
                  variant="outlined"
                  required
                  {...register('header_text', {
                    required: 'Header text is required',
                  })}
                  inputProps={{ maxLength: 40 }}
                  error={!!errors.header_text}
                  helperText={errors.header_text?.message}
                  autoComplete="off"
                />
                <TextField
                  id="body_text"
                  label="Body Text"
                  variant="outlined"
                  required
                  {...register('body_text', {
                    required: 'Body text is required',
                  })}
                  inputProps={{ maxLength: 80 }}
                  error={!!errors.body_text}
                  helperText={errors.body_text?.message}
                  multiline
                  rows={4}
                  autoComplete="off"
                />
                <Tooltip title="Unit of delay duration is in seconds" placement="bottom">
                  <TextField
                    id="delay_duration"
                    label="Delay Duration"
                    variant="outlined"
                    required
                    {...register('delay_duration', {
                      required: 'Delay Duration is required',
                    })}
                    inputProps={{ min: 0 }}
                    type="number"
                    error={!!errors.delay_duration}
                    helperText={errors.delay_duration?.message}
                    autoComplete="off"
                  />
                </Tooltip>
                <Autocomplete
                  id="display_mode"
                  options={['appear_once', 'persistence']}
                  value={displayMode}
                  onChange={(_, newValue) => {
                    setDisplayMode(newValue as DisplayMode);
                  }}
                  renderInput={(params) => <TextField {...params} label="Display Mode" required />}
                />
              </Stack>
            </Grid>
            <Grid item xs={12}>
              {isPromoMetaLoading ? (
                <Skeleton variant="rectangular" height={100} />
              ) : (
                <PromoDisplayConfigRules
                  ruleJSON={ruleJSON}
                  promoMeta={promoMeta}
                  promoDisplayConfigId={editableConfig?.promo_display_config_id || ''}
                  onRuleJsonUpdate={(ruleJSON) => {
                    setRuleJSON(ruleJSON);
                  }}
                  promoCodeName={editableConfig?.code_name || ''}
                />
              )}
            </Grid>
          </Grid>

          <Accordion key="legacyFields" defaultExpanded={false}>
            <AccordionSummary expandIcon={<Expand />}>
              <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                Legacy Fields
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6">Conditions</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Stack spacing={2}>
                    <Tooltip
                      title="[Global Conditions or displayOptions, if set, will override this legacy field] The promo popup will only be displayed to users if they are viewing an offer with a offer_mpv_threshold below the configured number (offer_mpv_threshold is a ratio of margin to views in the last 7 days) "
                      placement="bottom"
                    >
                      <TextField
                        id="offer_mpv_threshold"
                        label="Offer MPV Threshold"
                        variant="outlined"
                        {...register('offer_mpv_threshold', {
                          pattern: {
                            value: /^[0-9]*\.?[0-9]*$/,
                            message: 'Offer MPV threshold should be a valid number',
                          },
                        })}
                        inputProps={{ min: 0 }}
                        type="number"
                        error={!!errors.offer_mpv_threshold}
                        helperText={errors.offer_mpv_threshold?.message}
                        autoComplete="off"
                      />
                    </Tooltip>
                  </Stack>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleClose('cancel')}>Cancel</Button>
          <Button variant="contained" type="submit">
            {isEditing ? 'Edit' : isCloning ? 'Clone' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default PromoDisplayConfigForm;
